import type { RecordingStatus, RecordingResult } from '../tour-recording/tour-recording-types';
import { uploadTourRecording } from '../tour-recording/tour-recording-uploader';
import { tourRecorder } from '../tour-recording/tour-recorder';
import { WebRecordingAudioMixer } from './web-recording-audio-mixer';
import {
    isMobileRecordingDevice,
    canUseDesktopDisplayMedia,
    isMobileRecordingUnsupported,
    getSupportedWebRecordingMimeType,
    resolveDefaultWebRecordingTitle,
    requestBrowserDisplayStream,
    createHiddenPreviewVideo,
    captureStreamThumbnailBlob
} from './web-recording-media-helpers';
import { globalUtils } from '@/utils/tools/globalUtils';

export { isMobileRecordingDevice, canUseDesktopDisplayMedia, isMobileRecordingUnsupported };

export interface StartWebRecordingOptions {
    includeMic?: boolean;
    title?: string;
    idPath?: string;
}

class WebInteractionRecorderService {
    private status: RecordingStatus = 'idle';
    private activeMode: 'desktop_display' | 'mobile_canvas' | null = null;
    private mediaRecorder: MediaRecorder | null = null;
    private displayStream: MediaStream | null = null;
    private mobileMicStream: MediaStream | null = null;
    private ownsMobileMicStream = false;
    private audioMixer: WebRecordingAudioMixer | null = null;
    private previewVideoEl: HTMLVideoElement | null = null;
    private chunks: Blob[] = [];
    private startTime = 0;
    private elapsedSeconds = 0;
    private timerInterval: ReturnType<typeof setInterval> | null = null;
    private currentTitle = 'Tương tác AI';
    private currentIdPath = '';
    private savedDocTitle = '';

    private setStatus(newStatus: RecordingStatus): void {
        this.status = newStatus;
        if (typeof window !== 'undefined') {
            window.dispatchEvent(
                new CustomEvent('tour-recording-status-change', {
                    detail: { status: newStatus, recorder: 'web_interaction', mode: this.activeMode }
                })
            );
        }
    }

    public getStatus(): RecordingStatus {
        return this.status;
    }

    public getElapsedTime(): number {
        if (this.activeMode === 'mobile_canvas') {
            return Math.max(this.elapsedSeconds, tourRecorder.getElapsedTime());
        }
        return this.elapsedSeconds;
    }

    public async startRecording(options: StartWebRecordingOptions = {}): Promise<boolean> {
        if (this.status === 'recording' || this.status === 'stopping') return false;

        this.currentIdPath = options.idPath || (typeof window !== 'undefined' ? (window as any).id_path : '') || '';
        this.currentTitle = options.title || resolveDefaultWebRecordingTitle();

        // NHÁNH 1: Trên Điện thoại (iOS Safari / Android Chrome) hoặc trình duyệt không có getDisplayMedia
        // -> Tự động dùng Mobile 360 Canvas Compositor (720p thích ứng Dọc/Ngang + AI Audio + Micro)
        if (!canUseDesktopDisplayMedia()) {
            return this.startMobileCanvasRecording(Boolean(options.includeMic));
        }

        // NHÁNH 2: Trên Máy tính (PC/Laptop) -> Dùng getDisplayMedia 1080p
        try {
            this.displayStream = await requestBrowserDisplayStream();
            const videoTracks = this.displayStream.getVideoTracks();
            if (videoTracks.length === 0) {
                throw new Error('Không tìm thấy luồng hình ảnh từ trình duyệt');
            }

            this.activeMode = 'desktop_display';
            this.previewVideoEl = createHiddenPreviewVideo(videoTracks[0]);

            this.audioMixer = new WebRecordingAudioMixer();
            const mixedAudioTracks = await this.audioMixer.initialize(this.displayStream, Boolean(options.includeMic));
            const combinedStream = new MediaStream([videoTracks[0], ...mixedAudioTracks]);

            const mimeType = getSupportedWebRecordingMimeType();
            this.chunks = [];
            this.mediaRecorder = new MediaRecorder(combinedStream, {
                ...(mimeType ? { mimeType } : {}),
                videoBitsPerSecond: 5_000_000,
                audioBitsPerSecond: 128_000
            });

            this.mediaRecorder.ondataavailable = (e: BlobEvent) => {
                if (e.data && e.data.size > 0) this.chunks.push(e.data);
            };

            videoTracks[0].onended = () => {
                if (this.status === 'recording') this.stopRecording();
            };

            this.mediaRecorder.start(1000);
            this.startTime = Date.now();
            this.elapsedSeconds = 0;
            this.setStatus('recording');
            this.startElapsedTimer();
            return true;
        } catch (err: any) {
            this.cleanup();
            this.setStatus('idle');
            if (err?.name !== 'NotAllowedError') {
                globalUtils?.general?.showToast?.(
                    err?.message || 'Không thể bắt đầu ghi hình trang web.',
                    'Ghi hình tương tác AI',
                    'error',
                    true,
                    4000
                );
            }
            return false;
        }
    }

    private async startMobileCanvasRecording(includeMic: boolean): Promise<boolean> {
        try {
            const audioCtx = tourRecorder.getOrCreateAudioContext();
            if (audioCtx && audioCtx.state === 'suspended') {
                await audioCtx.resume().catch(() => {});
            }

            if (includeMic && typeof window !== 'undefined') {
                const existingMic = (window as any).__geminiMicStream as MediaStream | undefined;
                if (existingMic && existingMic.active && existingMic.getAudioTracks().length > 0) {
                    this.mobileMicStream = existingMic;
                    this.ownsMobileMicStream = false;
                } else if (navigator.mediaDevices?.getUserMedia) {
                    try {
                        this.mobileMicStream = await navigator.mediaDevices.getUserMedia({
                            audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
                            video: false
                        });
                        this.ownsMobileMicStream = true;
                    } catch {
                        // Tiếp tục ghi hình âm thanh AI & 360 ngay cả khi người dùng từ chối quyền Micro
                    }
                }
                if (this.mobileMicStream) {
                    tourRecorder.registerExternalMediaStream(this.mobileMicStream);
                }
            }

            const started = await tourRecorder.startRecording({
                tourName: this.currentTitle,
                idPath: this.currentIdPath,
                recordingType: 'ai_interaction',
                suppressSceneAudio: true,
                onError: (msg) => {
                    globalUtils?.general?.showToast?.(msg, 'Ghi hình tương tác AI', 'warning', true, 4000);
                }
            });

            if (!started) {
                this.cleanupMobileMic();
                return false;
            }

            this.activeMode = 'mobile_canvas';
            this.startTime = Date.now();
            this.elapsedSeconds = 0;
            this.setStatus('recording');
            this.startElapsedTimer();
            return true;
        } catch (err: any) {
            this.cleanupMobileMic();
            this.setStatus('idle');
            globalUtils?.general?.showToast?.(
                err?.message || 'Không thể bắt đầu ghi hình trên thiết bị di động.',
                'Ghi hình tương tác AI',
                'error',
                true,
                4000
            );
            return false;
        }
    }

    private startElapsedTimer(): void {
        if (this.timerInterval) clearInterval(this.timerInterval);
        this.timerInterval = setInterval(() => {
            this.elapsedSeconds = Math.max(0, Math.floor((Date.now() - this.startTime) / 1000));
            if (typeof window !== 'undefined') {
                window.dispatchEvent(
                    new CustomEvent('tour-recording-time-update', {
                        detail: { elapsedTime: this.elapsedSeconds, recorder: 'web_interaction' }
                    })
                );
                if (typeof document !== 'undefined') {
                    const mins = Math.floor(this.elapsedSeconds / 60);
                    const secs = this.elapsedSeconds % 60;
                    const formatted = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
                    if (!this.savedDocTitle) {
                        this.savedDocTitle = document.title;
                    }
                    document.title = `🔴 REC ${formatted} | ${this.savedDocTitle}`;
                }
            }
        }, 1000);
    }

    public async stopRecording(): Promise<RecordingResult | null> {
        if (this.status !== 'recording') return null;

        if (this.activeMode === 'mobile_canvas') {
            this.setStatus('stopping');
            const rawResult = await tourRecorder.stopRecording(false);
            this.cleanup();
            if (!rawResult) {
                this.setStatus('idle');
                return null;
            }
            const aiResult: RecordingResult = {
                ...rawResult,
                tourName: this.currentTitle || rawResult.tourName || 'Tương tác AI',
                idPath: this.currentIdPath || rawResult.idPath,
                recordingType: 'ai_interaction'
            };
            await this.uploadRecordingResult(aiResult);
            return aiResult;
        }

        if (!this.mediaRecorder) return null;
        this.setStatus('stopping');
        const duration = Math.max(1, Math.round((Date.now() - this.startTime) / 1000));
        const thumbnailBlob = await captureStreamThumbnailBlob(this.previewVideoEl);

        return new Promise<RecordingResult | null>((resolve) => {
            const recorder = this.mediaRecorder!;
            const mimeType = recorder.mimeType || 'video/webm';

            recorder.onstop = async () => {
                const finalBlob = new Blob(this.chunks, { type: mimeType });
                const idPath = this.currentIdPath || (typeof window !== 'undefined' ? (window as any).id_path : '');
                const tourName = this.currentTitle || 'Tương tác AI';
                this.cleanup();

                if (duration < 5 || finalBlob.size < 1000) {
                    this.setStatus('idle');
                    globalUtils?.general?.showToast?.(
                        'Video ghi hình quá ngắn (tối thiểu 5 giây). Vui lòng quay lâu hơn để lưu video!',
                        'Ghi hình tương tác AI',
                        'warning',
                        true,
                        4500
                    );
                    resolve(null);
                    return;
                }

                const result: RecordingResult = {
                    blob: finalBlob,
                    duration,
                    tourName,
                    idPath,
                    fileSize: finalBlob.size,
                    thumbnailBlob,
                    recordingType: 'ai_interaction'
                };
                await this.uploadRecordingResult(result);
                resolve(result);
            };

            try {
                if (recorder.state === 'recording') {
                    recorder.requestData();
                    recorder.stop();
                } else {
                    this.cleanup();
                    this.setStatus('idle');
                    resolve(null);
                }
            } catch {
                this.cleanup();
                this.setStatus('idle');
                resolve(null);
            }
        });
    }

    private async uploadRecordingResult(result: RecordingResult): Promise<void> {
        try {
            this.setStatus('uploading');
            await uploadTourRecording(result);
            this.setStatus('completed');
            setTimeout(() => {
                if (this.status === 'completed') this.setStatus('idle');
            }, 2000);
        } catch (uploadErr: any) {
            this.setStatus('error');
            globalUtils?.general?.showToast?.(
                uploadErr?.message || 'Lỗi khi tải video tương tác AI lên máy chủ',
                'Ghi hình tương tác AI',
                'error',
                true,
                4500
            );
            setTimeout(() => this.setStatus('idle'), 3000);
        }
    }

    private cleanupMobileMic(): void {
        if (this.mobileMicStream) {
            tourRecorder.unregisterExternalMediaStream(this.mobileMicStream);
            if (this.ownsMobileMicStream) {
                this.mobileMicStream.getTracks().forEach((t) => t.stop());
            }
            this.mobileMicStream = null;
            this.ownsMobileMicStream = false;
        }
    }

    private cleanup(): void {
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
            this.timerInterval = null;
        }
        if (this.savedDocTitle && typeof document !== 'undefined') {
            document.title = this.savedDocTitle;
            this.savedDocTitle = '';
        }
        this.cleanupMobileMic();
        if (this.previewVideoEl) {
            try {
                this.previewVideoEl.pause();
                this.previewVideoEl.srcObject = null;
            } catch {}
            this.previewVideoEl = null;
        }
        if (this.displayStream) {
            this.displayStream.getTracks().forEach((track) => {
                track.onended = null;
                track.stop();
            });
            this.displayStream = null;
        }
        if (this.audioMixer) {
            this.audioMixer.cleanup();
            this.audioMixer = null;
        }
        this.mediaRecorder = null;
        this.activeMode = null;
        this.chunks = [];
    }
}

export const webInteractionRecorder = new WebInteractionRecorderService();

if (typeof window !== 'undefined') {
    (window as any).webInteractionRecorder = webInteractionRecorder;
}
